// The platform shell: the router, the theme, the session, and which module a path
// belongs to (spec, Routing and landing).
import { Component, useMemo } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useHref, useNavigate } from 'react-router';
import { RouterProvider } from 'react-aria-components';

import { ThemeProvider } from './theme/ThemeProvider.jsx';
import { SessionProvider, useSession } from './session/SessionProvider.jsx';
import { api } from '../src/api/client.js';
import { REGISTERED_MODULES } from './registry.js';
import {
  availableModules, pickLanding, noAccessReason, moduleUnavailableReason, deadEndHelp,
} from './module-logic.js';
import { createLastModuleSaver } from './last-module.js';
import { NoAccessSignedOut, NoAccessSignedIn } from './NoAccess.jsx';
import ModuleUnavailable from './ModuleUnavailable.jsx';
import ModuleHost from './ModuleHost.jsx';
import PlatformLayout from './PlatformLayout.jsx';
import ShellNotFound from './ShellNotFound.jsx';
// The kit's platform-wide hosts, imported by FILE: the shell never imports the kit
// barrel (test/web/shell-kit-imports.test.js). It imports no React Aria.
import ToastRegion from '../platform/ui/toast/ToastRegion.jsx';
// ...and the confirm host: it imports no React Aria either (confirm()'s dialog is a lazy chunk).
import ConfirmHost from '../platform/ui/overlay/ConfirmHost.jsx';

function ModuleRoute({ manifest, available, remember }) {
  const user = useSession();
  if (!available.includes(manifest)) {
    return (
      <PlatformLayout active={null} available={available} entry={null}>
        <ModuleUnavailable manifest={manifest} reason={moduleUnavailableReason(manifest, user)}
          help={deadEndHelp(available, remember.current())} />
      </PlatformLayout>
    );
  }
  return <ModuleHost manifest={manifest} available={available} remember={remember} />;
}

// "/" sends the person to their landing module. Decided when "/" is RENDERED, from the
// saver's in-memory copy (spec, Routing: "the in-memory copy updated at once"), so someone
// who switched modules and then follows a link to "/" lands where they last were, not
// where GET /api/me said they were at sign-in.
function Landing({ available, remember }) {
  return <Navigate replace to={pickLanding(available, remember.current()).home} />;
}

function PlatformRoutes() {
  const user = useSession();
  const available = useMemo(() => availableModules(REGISTERED_MODULES, user), [user]);
  // One saver per signed-in person, not per session object: an identity re-check that
  // hands back a fresh object for the SAME person must not reset the in-memory copy to
  // the stale server value, nor drop a save in flight. A different person reloads the
  // page (SessionProvider), so the id is all this needs to follow.
  const userId = user.id;
  const serverLastModule = user.last_module;
  const remember = useMemo(
    () => createLastModuleSaver((id) => api.put('/me/last-module', { module: id }), serverLastModule),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userId],
  );

  if (available.length === 0) return <NoAccessSignedIn reason={noAccessReason(user, REGISTERED_MODULES)} />;

  const root = REGISTERED_MODULES.find((mf) => mf.basePath === '/');
  const prefixed = REGISTERED_MODULES.filter((mf) => mf.basePath !== '/');

  // Each module's element is KEYED by its id: a switch must unmount the old module's host
  // (its Provider, header actions and routes) and mount a fresh one, never reuse the old
  // component under the new manifest. web/shell/module-entry.js is the second guard.
  return (
    <Routes>
      <Route path="/" element={<Landing available={available} remember={remember} />} />
      {prefixed.map((mf) => (
        <Route key={mf.id} path={`${mf.basePath}/*`}
          element={<ModuleRoute key={mf.id} manifest={mf} available={available} remember={remember} />} />
      ))}
      {/* No registered module is a root module (Support lives at /support), so an
          address outside every module is the shell's own not-found page. The root
          branch stays for the generic contract, which still allows one. */}
      <Route path="*" element={root
        ? <ModuleRoute key={root.id} manifest={root} available={available} remember={remember} />
        : (
          <PlatformLayout active={null} available={available} entry={null}>
            <ShellNotFound help={deadEndHelp(available, remember.current())} />
          </PlatformLayout>
        )} />
    </Routes>
  );
}

// Every kit Link, and every kit Button given an href, navigates through react-router
// instead of reloading the page (spec, The platform UI kit: Links). Wired ONCE, here,
// inside BrowserRouter so useNavigate has a router.
function AriaRouter({ children }) {
  const navigate = useNavigate();
  return <RouterProvider navigate={navigate} useHref={useHref}>{children}</RouterProvider>;
}

// The last line of defence: anything that throws outside a module's page and outside
// the header slots (each has its own boundary) lands here instead of blanking the tab.
// That is chiefly a module's Provider, which has no boundary of its own (the header is
// outside it since D-046, but a boundary there would need a fallback for the page's
// place: PlatformLayout.jsx), and the shell's own code. Plain markup and no router, theme or session: any of those
// may be what broke.
class RootBoundary extends Component {
  constructor(props) { super(props); this.state = { failed: false }; }
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(error) { console.error('[shell] the app crashed', error); }
  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <main className="grid min-h-screen place-items-center bg-canvas px-6 text-center text-ink">
        <div>
          <h1 className="text-lg font-semibold">Something went wrong</h1>
          <button type="button" className="btn-primary mt-4" onClick={() => window.location.reload()}>Reload</button>
        </div>
      </main>
    );
  }
}

export default function Shell() {
  return (
    <RootBoundary>
      <BrowserRouter>
        <AriaRouter>
          <ThemeProvider>
            <Routes>
              {/* Before the session: a refused sign-in has no session, and calling /api/me
                  from here would 401 straight back into sign-in. */}
              <Route path="/no-access" element={<NoAccessSignedOut />} />
              <Route path="*" element={<SessionProvider><PlatformRoutes /></SessionProvider>} />
            </Routes>
            {/* One toast region and one confirm host for every module (spec §2, §3). */}
            <ToastRegion />
            <ConfirmHost />
          </ThemeProvider>
        </AriaRouter>
      </BrowserRouter>
    </RootBoundary>
  );
}
